// Product events are created by our own browser analytics, never PostHog.
// Strip sensitive fields before the authenticated first-party request.
// The sender (not telemetry-proxy) decides what browser event context is safe
// to forward. New business properties are retained automatically.
const PRIVATE_KEY =
    /(?:email|password|secret|token|authorization|access_key|api_key|device_name|ip_address|current_url|referrer_url|error_message|error_stack|user_agent)/i;
const MAX_PROPERTIES = 100;
const MAX_DEPTH = 5;
function safeBrowserValue(value: unknown, depth = 0): unknown {
    if (depth > MAX_DEPTH) {
        return undefined;
    }
    if (value === null || typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value))) {
        return value;
    }
    if (typeof value === "string") {
        if (value.length > 2000) {
            return undefined;
        }
        // Don't pass raw URLs, addresses or arbitrary local paths in property strings.
        if (value.includes("://") || value.includes("@")) {
            return undefined;
        }
        return value;
    }
    if (Array.isArray(value)) {
        if (value.length > 40) {
            return undefined;
        }
        return value.map((item) => safeBrowserValue(item, depth + 1)).filter((item) => item !== undefined);
    }
    if (value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
        const result: Record<string, unknown> = {};
        for (const [key, item] of Object.entries(value).slice(0, MAX_PROPERTIES)) {
            if (PRIVATE_KEY.test(key) || key.startsWith("$") || key.length > 128) {
                continue;
            }
            const next = safeBrowserValue(item, depth + 1);
            if (next !== undefined) {
                result[key] = next;
            }
        }
        return result;
    }
    return undefined;
}

/** Attribute OAuth clients without sending raw redirect URLs. */
export function oauthClientHostname(value: unknown): string | undefined {
    if (typeof value !== "string") {
        return undefined;
    }
    try {
        const url = new URL(value);
        if (
            ![
                "https:",
                "http:",
            ].includes(url.protocol) ||
            url.username ||
            url.password
        ) {
            return undefined;
        }
        const host = url.hostname.toLowerCase();
        return host && host.length <= 253 && /^[a-z0-9.-]+$/.test(host) ? host : undefined;
    } catch {
        return undefined;
    }
}

export type BrowserProductEvent = {
    event_id: string;
    name: string;
    occurred_at: string;
    browser_session_id?: string;
    oauth_session_id?: string;
    host?: string;
    pathname?: string;
    referrer_host?: string;
    properties: Record<string, unknown>;
};

export type BrowserEventContext = {
    eventId: string;
    timestamp: string;
    browserSessionId: string;
    hostname: string;
    pathname: string;
    search: string;
    referrer: string;
};

const pending: BrowserProductEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let sending = false;

/** Only native browser context is used; no SDK enrichment or PostHog globals. */
export function buildBrowserProductEvent(
    name: string,
    properties: Record<string, unknown>,
    context: BrowserEventContext,
): BrowserProductEvent | null {
    if (
        !/^[$a-zA-Z_][a-zA-Z0-9_$.-]{0,95}$/.test(name) ||
        !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(context.eventId) ||
        !Number.isFinite(Date.parse(context.timestamp))
    ) {
        return null;
    }

    let oauthSessionId: string | undefined;
    try {
        const candidate = new URLSearchParams(context.search).get("session_id");
        if (candidate && /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(candidate)) {
            oauthSessionId = candidate;
        }
    } catch {
        /* Invalid URL parameters cannot stop analytics. */
    }

    let referrerHost: string | undefined;
    try {
        if (context.referrer) {
            referrerHost = new URL(context.referrer).hostname;
        }
    } catch {
        /* Never transmit the full referrer. */
    }

    const normalizedPath = context.pathname.startsWith("/") ? context.pathname : "/";
    return {
        event_id: context.eventId,
        name,
        occurred_at: new Date(context.timestamp).toISOString(),
        browser_session_id: context.browserSessionId,
        ...(oauthSessionId
            ? {
                  oauth_session_id: oauthSessionId,
              }
            : {}),
        host: context.hostname,
        pathname: normalizedPath,
        ...(referrerHost
            ? {
                  referrer_host: referrerHost,
              }
            : {}),
        properties: safeBrowserValue(properties) as Record<string, unknown>,
    };
}

let retryWaitMs = 500;

async function flush() {
    if (sending || !pending.length) {
        return;
    }
    sending = true;
    flushTimer = undefined;
    const events = pending.splice(0, 20);
    let retry = false;
    try {
        // Account identity comes from the server session, not the browser body.
        const response = await fetch("/auth/backend/analytics/events", {
            method: "POST",
            credentials: "include",
            keepalive: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                events,
            }),
        });
        retry = [
            408,
            425,
            429,
            500,
            502,
            503,
            504,
        ].includes(response.status);
    } catch {
        retry = true;
    } finally {
        sending = false;
        if (retry) {
            pending.unshift(...events);
            retryWaitMs = Math.min(retryWaitMs * 2, 30_000);
        } else {
            retryWaitMs = 500;
        }
        if (pending.length) {
            schedule(retry ? retryWaitMs : 350);
        }
    }
}

function schedule(delay = 350) {
    if (!flushTimer && !sending) {
        flushTimer = setTimeout(() => void flush(), delay);
    }
}

/** Only admission to a bounded in-memory queue; not a durable acknowledgement. */
export function enqueueBrowserProductEvent(event: BrowserProductEvent): boolean {
    if (pending.length >= 200) {
        return false;
    }
    pending.push(event);
    // Google OAuth redirects immediately; start keepalive delivery before leaving the page.
    if (event.name === "auth_google_oauth_started") {
        if (flushTimer) {
            clearTimeout(flushTimer);
        }
        flushTimer = undefined;
        void flush();
    } else if (pending.length >= 20 && !sending) {
        if (flushTimer) {
            clearTimeout(flushTimer);
        }
        flushTimer = undefined;
        void flush();
    } else {
        schedule();
    }
    return true;
}
